/**
 * Хуки данных каталога удобств + пака иконок (правки Артёма 2026-06-17).
 * Tanstack Query поверх @hsc/api-client. Server-state только через React
 * Query (патент проекта).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAmenity,
  deleteAmenity,
  listAmenities,
  listIcons,
  updateAmenity,
} from '@hsc/api-client';
import type {
  AmenityView,
  CreateAmenityInput,
  IconView,
  UpdateAmenityInput,
} from '@hsc/types';

/** GET /v1/icons — глобальный пак иконок (для пикера). Кэш надолго. */
export function useIcons() {
  return useQuery<IconView[]>({
    queryKey: ['icons'],
    queryFn: listIcons,
    staleTime: 30 * 60_000,
  });
}

/** GET /v1/amenities — каталог удобств тенанта. */
export function useAmenities() {
  return useQuery<AmenityView[]>({
    queryKey: ['amenities'],
    queryFn: listAmenities,
    staleTime: 60_000,
  });
}

/** POST /v1/amenities — завести своё удобство. */
export function useCreateAmenity() {
  const qc = useQueryClient();
  return useMutation<AmenityView, Error, CreateAmenityInput>({
    mutationFn: createAmenity,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['amenities'] });
    },
  });
}

/** PUT /v1/amenities/{id} — изменить название/иконку. */
export function useUpdateAmenity() {
  const qc = useQueryClient();
  return useMutation<AmenityView, Error, { id: string; input: UpdateAmenityInput }>({
    mutationFn: ({ id, input }) => updateAmenity(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['amenities'] });
    },
  });
}

/** DELETE /v1/amenities/{id} — удалить удобство. */
export function useDeleteAmenity() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteAmenity,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['amenities'] });
    },
  });
}

/** Карта id→IconView для быстрого рендера иконок удобств. */
export function iconMap(icons: readonly IconView[] | undefined): Map<string, IconView> {
  const m = new Map<string, IconView>();
  for (const ic of icons ?? []) m.set(ic.id, ic);
  return m;
}
